import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import { observer } from 'mobx-react-lite'
import { Button, Popconfirm, Popover, Select, Space, Tag, Tooltip } from 'antd'
import { UserOutlined, InfoOutlined } from '@ant-design/icons'

import {
	actLoadWorkflow,
	actSaveWorkflow,
	newWorkflow,
	requireWorkflowById
} from '../../../stores/workflows/actions'
import {
	actUndoOrDeleteWorkflow,
	actLoadWorkflows
} from '../../../stores/ui-lst-workflows/actions'
import { useStores } from '../../../cls/stores/MobXProvider'

import {
	StatusTag,
	Filter,
	UserString,
	LstTable,
	FormatedTimestamp,
	BetweenDates,
	CustomTable
} from '../../../components/Shared'
import { recurrenceLabels } from '../../../components/Form/recurrence/labels'
import ProgressModal from '../../../components/Form/ProgressModal'
import { runInAction } from 'mobx'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import { DueIn } from '../../../components/Shared/FormatedTimestamp'
import dayjs from 'dayjs'
import { sendNotification } from '../../../stores/notifications/action'
import { translate as t } from '../../../cls/TranslationContext'
import { useSmartTranslation } from '../../../cls/TranslationContext'

/**
 * @function LstItem
 * @description Renders a single row in the workflows list. Displays workflow details like name, priority,
 * progress, assigned user, recurrence status, and action buttons. Handles user-based permission logic for
 * visibility and allowed actions (view, edit, delete, status change).
 *
 * @param {object} props
 * @param {string} props.id - Workflow ID to render.
 * @param {boolean} props.isFirst - Whether this is the first item in the list (used for tour anchors).
 * @returns {JSX.Element}
 */
export const LstItem = observer(({ id, isFirst }) => {
	const { t } = useSmartTranslation()

	const navigate = useNavigate()
	const { uiLstWorkflows, users, categories } = useStores()
	const { loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const workflow = requireWorkflowById(id)
	const selfPermission =
		workflow.createdBy === loggedInUser.user_id || isAdmin || isSuperAdmin

	const hasRecurrence = !!workflow.recurrence.next

	// Delete is active only for the creator of the workflow if is in draft mode or if the user is an admin
	const deleteIsDisabled = !isAdmin
		? !(
				workflow.progress === 'draft' &&
				loggedInUser.user_id === workflow.createdBy
		  )
		: false

	const [showConfirmUndoOrDelete, setConfirmUndoOrDelete] = useState(false)
	/**
	 * @function confirmUndoOrDelete
	 * @description Executes the deletion or undeletion of a workflow item after user confirmation.
	 */
	const confirmUndoOrDelete = () => {
		setConfirmUndoOrDelete(false)

		if (!!workflow.to_delete) {
			actUndoOrDeleteWorkflow(id, false).catch(console.error)
		} else {
			actUndoOrDeleteWorkflow(id, true).catch(console.error)
		}
	}
	const _actions = [
		{
			key: 'status',
			label: t('single.set-progress'),
			value: 'status',
			disabled: !selfPermission && !users.canUser('write', 'workflows')
		},
		{
			key: 'view',
			label: t('single.view'),
			value: 'view',
			disabled: !selfPermission && !users.canUser('read', 'workflows')
		},
		{
			key: 'data',
			label: t('__edit%'),
			value: 'data',
			disabled:
				(!!workflow.to_delete || !selfPermission) &&
				!users.canUser('write', 'workflows')
		}
	]

	// Delete / Undo actions
	if (selfPermission) {
		if (workflow.to_delete) {
			_actions.push({
				key: 'undo-del',
				label: t('single.delete-undo'),
				value: 'undo-delete',
				disabled:
					deleteIsDisabled && !users.canUser('delete', 'workflows')
			})
		} else {
			_actions.push({
				key: 'del',
				label: t('single.delete'),
				value: 'delete',
				disabled:
					deleteIsDisabled && !users.canUser('delete', 'workflows')
			})
		}
	}

	/**
	 * @function handleActions
	 * @description Handles dropdown actions for a workflow item. Supported actions include:
	 * 'view', 'data' (edit), 'delete', 'undo-delete', and 'status' (change progress).
	 *
	 * @param {string} value - Action key to perform.
	 */
	const handleActions = (value) => {
		switch (value) {
			case 'view':
				navigate(`${id}/view`)
				break
			case 'data':
				navigate(`${id}/edit`)
				break
			case 'undo-delete':
				setConfirmUndoOrDelete(true)
				break
			case 'delete':
				setConfirmUndoOrDelete(true)
				break
			case 'status':
				runInAction(() => {
					uiLstWorkflows.setModal('workflow-progress', {
						visible: true,
						itmId: id
					})
				})
				break
			default:
				break
		}
	}

	return (
		<tr>
			<td>
				<CustomTable
					data={[
						[
							{
								item: (
									<Space style={{ width: '100%' }}>
										<StatusTag
											id={
												isFirst
													? 'target-itm-priority'
													: ''
											}
											tag={workflow.priority}
											tagType={'PriorityTag'}
											style={{
												marginRight: 0,
												padding: '0 3px'
											}}
										/>
										<StatusTag
											isShort
											id={
												isFirst
													? 'target-itm-progress'
													: ''
											}
											tag={workflow.progress}
											style={{
												marginRight: 0,
												padding: '0 3px'
											}}
										/>
										<Tooltip title={t('single.view')}>
											<Link
												to={`${id}/view`}
												id={
													isFirst
														? 'target-itm-link'
														: ''
												}
											>
												{workflow.name}
											</Link>
										</Tooltip>
									</Space>
								)
							}
						]
					]}
				/>
			</td>
			<td>
				<CustomTable
					data={[
						[
							{
								item:
									loggedInUser.user_id ===
									workflow.for_user_id ? (
										<Tooltip title={t('__assigned-to')}>
											<UserOutlined
												id={
													isFirst
														? 'target-itm-user'
														: ''
												}
											/>
										</Tooltip>
									) : (
										<Tooltip title={t('__assigned%_user')}>
											<Tag
												color="default"
												id={
													isFirst
														? 'target-itm-user'
														: ''
												}
											>
												<UserString
													userId={
														workflow.for_user_id
													}
													path="name"
												/>
											</Tag>
										</Tooltip>
									)
							}
						],
						[
							{
								item: (
									<Tooltip
										title={t('__field%_category.single')}
									>
										<span
											id={
												isFirst
													? 'target-itm-category'
													: ''
											}
										>
											{categories[workflow?.category_id]
												?.name || '-'}
										</span>
									</Tooltip>
								)
							}
						]
					]}
				/>
			</td>
			<td id={isFirst ? 'target-itm-recurrence' : ''}>
				{workflow.recurrence.type && (
					<CustomTable
						data={[
							[
								t('single.next'),
								{
									item: workflow.recurrence.next ? (
										<FormatedTimestamp
											timestamp={workflow.recurrence.next}
										/>
									) : workflow.progress !== 'completed' ? (
										'*requires update'
									) : (
										'completed'
									)
								}
							],
							workflow.recurrence.next &&
								workflow.progress !== 'completed' && [
									t('single.in'),
									{
										item: (
											<DueIn
												timestamp={
													workflow.recurrence.next
												}
											/>
										)
									}
								]
						]}
					/>
				)}
			</td>

			<td>
				<Space style={{ width: '100%' }} direction="vertical">
					<Space>
						<Popover
							content={() => (
								<CustomTable
									data={[
										[
											{
												item: (
													<strong>
														{t('single.info')}
													</strong>
												)
											}
										],
										[
											t('single.updated-at'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															workflow.modifiedTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											t('single.created-at'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															workflow.createdTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											t('single.created-by.label'),
											{
												item: (
													<UserString
														userId={
															workflow.createdBy
														}
														path="name"
													/>
												)
											}
										],
										[
											t('__assigned-to'),
											{
												item: (
													<UserString
														userId={
															workflow.for_user_id
														}
														path="name"
													/>
												)
											}
										],
										hasRecurrence && [
											{
												item: (
													<strong>
														{t('_recurrence')}
													</strong>
												)
											}
										],
										hasRecurrence && [
											'Type:',
											{
												item:
													recurrenceLabels[
														workflow.recurrence.type
													] ||
													'*' +
														workflow.recurrence.type
											}
										],
										hasRecurrence && [
											t('single.next'),
											{
												item: workflow.recurrence
													.next ? (
													<FormatedTimestamp
														timestamp={
															workflow.recurrence
																.next
														}
													/>
												) : workflow.progress !==
												  'completed' ? (
													'*requires update'
												) : (
													'completed'
												)
											}
										],
										hasRecurrence &&
											workflow.recurrence.type !==
												'deadline' && [
												'Start/End:',
												{
													item: (
														<BetweenDates
															timestamps={
																workflow
																	.recurrence
																	.startEnd
															}
														/>
													)
												}
											]
									]}
								/>
							)}
							trigger="hover"
						>
							<Button
								id={isFirst ? 'target-itm-info' : ''}
								color="default"
								icon={<InfoOutlined />}
							/>
						</Popover>
						<Select
							id={isFirst ? 'target-itm-actions' : ''}
							value="Actions"
							options={_actions}
							onChange={handleActions}
							style={{ width: 120 }}
						/>
					</Space>
					{(selfPermission ||
						users.canUser('delete_view', 'workflows')) &&
						workflow.toDeleteTs && (
							<>
								<span>
									{t('single.delete-on')}
									<FormatedTimestamp
										timestamp={workflow.toDeleteTs}
									/>
								</span>
							</>
						)}
				</Space>

				<Popconfirm
					title={
						workflow.to_delete
							? t('single.confirm-undo-delete')
							: t('single.confirm-delete')
					}
					open={showConfirmUndoOrDelete}
					onConfirm={confirmUndoOrDelete}
					onCancel={() => setConfirmUndoOrDelete(false)}
					okType="link"
					placement="topLeft"
				/>
			</td>
		</tr>
	)
})

LstItem.propTypes = {
	id: PropTypes.string.isRequired,
	isFirst: PropTypes.bool
}

export const workflowsLstProgressOptions = [
	{
		key: 'by-progress',
		value: '',
		label: t('__by%single.status')
	},
	{
		key: 'by-progress-canceled',
		value: 'canceled',
		label: t('single.canceled')
	},
	{
		key: 'by-progress-draft',
		value: 'draft',
		label: t('single.draft')
	},
	{
		key: 'by-progress-in_review',
		value: 'in_review',
		label: t('single.in-review')
	},
	{
		key: 'by-progress-active',
		value: 'active',
		label: t('single.active')
	},
	{
		key: 'by-progress-pending',
		value: 'pending',
		label: t('single.pending')
	},
	{
		key: 'by-progress-completed',
		value: 'completed',
		label: t('single.completed')
	}
]

/**
 * @function WorkflowsLst
 * @description Displays a full page with filters and a paginated table of workflows. Integrates filtering,
 * periodic refresh, deletion, user permissions, and onboarding tour capabilities. Supports progress status editing
 * and inline creation of demo items for the tour.
 *
 * @returns {JSX.Element}
 */
const WorkflowsLst = observer(() => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { uiLstWorkflows, tourStore, hasIntervalLoad, users } = useStores()
	const { loggedInUser, loggedInUserId } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin
	const pg = uiLstWorkflows.pg
	const bySearch = uiLstWorkflows.search
	const byDel = uiLstWorkflows.del
	const byCategory = uiLstWorkflows.filter.category
	const byProgress = uiLstWorkflows.filter.progress
	const byPriority = uiLstWorkflows.filter.priority
	const for_user_uid = uiLstWorkflows.filter.for_user_uid
	const user_uid = uiLstWorkflows.filter.user_uid
	const orderBy = uiLstWorkflows.filter.orderBy
	const orderDir = uiLstWorkflows.filter.orderDir

	const intervalIdRef = useRef(null)
	const [errorKey] = useState(null)
	const withIntervalLoad = hasIntervalLoad('workflows')

	/**
	 * @function cancelCheckAndLoad
	 * @description Clears the periodic interval loading reference.
	 */
	useEffect(() => {
		actLoadWorkflows().catch(console.error)

		const cancelCheckAndLoad = () => {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current)
			}
		}

		/**
		 * @function checkAndLoadItems
		 * @description Periodically refreshes the workflow list if interval loading is enabled.
		 * Clears the interval if not required or an error prevents updates.
		 */
		const checkAndLoadItems = () => {
			if (withIntervalLoad) {
				if (errorKey !== 'not_found') {
					actLoadWorkflows(true)
						.then(
							(res) =>
								res && actLoadWorkflows().catch(console.error)
						)
						.catch(console.error)
				} else if (errorKey === 'not_found') {
					cancelCheckAndLoad()
				}
			} else {
				cancelCheckAndLoad()
			}
		}

		if (withIntervalLoad) {
			intervalIdRef.current = setInterval(checkAndLoadItems, 5000)
		} else {
			cancelCheckAndLoad()
		}

		return () => cancelCheckAndLoad()
	}, [
		pg,
		bySearch,
		byDel,
		byCategory,
		byProgress,
		byPriority,
		withIntervalLoad,
		user_uid,
		for_user_uid,
		orderBy,
		orderDir
	])

	const hideTourItem = []
	const hideFilters = []
	if (
		!(isAdmin || isSuperAdmin || users.canUser('delete_view', 'workflows'))
	) {
		hideFilters.push('deleted')
		hideTourItem.push('delete')
	}

	if (!(isAdmin || isSuperAdmin || users.canUser('write', 'workflows'))) {
		hideFilters.push('addNew')
		hideTourItem.push('new')
	}

	const tourWorkflowLst = getTourArrayByKeys('workflowLst', ['all'])
	const tourFilter = getTourArrayByKeys(
		'filter',
		[
			'section',
			'search',
			'progress',
			'priority',
			'category',
			'new',
			'toggleBtn',
			'ByUser',
			'ForUser',
			'orderBy',
			'delete'
		],
		hideTourItem
	)

	/**
	 * @function startTour
	 * @description Starts the interactive onboarding tour for the workflow list view. If the list is empty,
	 * it creates a demo item to ensure UI elements exist for the tour.
	 */
	const startTour = () => {
		runInAction(() => {
			uiLstWorkflows.filterExpanded = true
		})

		const hasItems = uiLstWorkflows.ids.length > 0
		if (!hasItems) {
			const newId = uuidv4()
			tourStore.setDeleteItemAct(() =>
				actUndoOrDeleteWorkflow(newId, true, true).catch(console.error)
			)

			const demoObj = {
				createdTs: dayjs().unix(),
				createdBy: loggedInUserId,
				for_user_id: loggedInUserId,
				platformIds: [],
				category_id: byCategory || '7',
				name: 'demoItem',
				progress: 'draft',
				recurrence: {
					daysOfWeek: [],
					dayOfMonth: '',
					months: [],
					next: ''
				},
				subtasks: [],
				comments: []
			}
			actSaveWorkflow(newId, demoObj, true).catch(console.error)
		}
		tourStore.startTour('WorkflowsLst-tour', [
			...tourFilter,
			...tourWorkflowLst
		])
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('single.planning') }}
				buttonProps={{
					onClick: startTour
				}}
				rowProps={{
					justify: 'space-between'
				}}
			></GeneralTourTitle>
			<Filter
				uiStore={uiLstWorkflows}
				hide={hideFilters}
				addNew={() => {
					const newId = uuidv4()
					newWorkflow(newId).then(() => {
						navigate(`/workflows/${newId}/edit`)
					})
				}}
				progressOptions={workflowsLstProgressOptions}
			/>

			<ProgressModal
				type="workflow"
				modalKey="workflow-progress"
				uiStore={uiLstWorkflows}
				requireItemById={requireWorkflowById}
				loadItm={(id) => actLoadWorkflow(id)}
				saveItem={(itemId, itemValue) => {
					actSaveWorkflow(itemId, itemValue)

					if (itemValue?.for_user_id != itemValue.createdBy) {
						const forUser = users.requireUserById(
							itemValue.for_user_id
						)
						sendNotification(
							loggedInUser,
							forUser?.email,
							itemValue?.for_user_id,
							'workflows',
							itemId,
							'progress'
						)
					}
				}}
			/>

			<LstTable
				LstItem={LstItem}
				store={uiLstWorkflows}
				className="table-pro"
			>
				<tr id="items-lst">
					<th id="target-workflow">{t('single.overview')}</th>
					<th id="target-info">{t('single.information')}</th>
					<th id="target-recurrence">
						{t('single.recurrence.label')}
					</th>
					<th id="target-actions">{t('single.actions')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})

export default WorkflowsLst
